import { watercraftApiSlice } from '../../store/apiSlices/watercraftApiSlice';
import type {
  SubscriptionStatusResponse,
  VerifyCheckoutRequest,
  WatercraftPrice,
} from './watercraftSlice';

export interface PassPrice extends WatercraftPrice {
  pass_count: number;
}

export interface PassPricesResponse {
  prices?: PassPrice[];
}

export interface PassCheckoutRequest {
  price_id: string;
  success_url: string;
  cancel_url: string;
}

export interface SessionPassCounts {
  available?: number;
  allocated?: number;
  redeemed?: number;
  expired?: number;
}

export interface SessionPass {
  id: string;
  status: string;
  source: string;
  acquired_at?: string | null;
  expires_at?: string | null;
}

export interface PassPurchaseVerificationResponse {
  verified: boolean;
  message?: string | null;
  credited_count?: number;
  passes?: SessionPass[];
  session_pass_counts?: SessionPassCounts;
}

export interface EntitlementsResponse {
  has_paid_access: boolean;
  subscription?: SubscriptionStatusResponse | null;
  session_pass_counts?: SessionPassCounts;
}

export const watercraftPassesSlice = watercraftApiSlice
  .enhanceEndpoints({ addTagTypes: ['WatercraftEntitlements'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getAccessEntitlements: builder.query<EntitlementsResponse, void>({
        query: () => ({
          url: 'user/access-entitlements',
          method: 'GET',
          useDefaultErrorHandler: false,
        }),
        providesTags: ['WatercraftEntitlements'],
      }),
      getPassPrices: builder.query<PassPricesResponse, void>({
        query: () => ({
          url: 'session-passes/stripe/prices',
          method: 'GET',
          useDefaultErrorHandler: false,
        }),
      }),
      createPassCheckoutSession: builder.mutation<
        string | undefined,
        PassCheckoutRequest
      >({
        query: (request) => ({
          url: 'session-passes/stripe/checkout',
          method: 'POST',
          data: request,
          useDefaultErrorHandler: false,
        }),
        transformResponse: (response: { redirect_url: string } | undefined) =>
          response?.redirect_url,
      }),
      verifyPassCheckout: builder.mutation<
        PassPurchaseVerificationResponse,
        VerifyCheckoutRequest
      >({
        query: (request) => ({
          url: 'session-passes/stripe/verify',
          method: 'POST',
          data: request,
          useDefaultErrorHandler: false,
        }),
        invalidatesTags: ['WatercraftEntitlements'],
      }),
    }),
  });

export const {
  useGetAccessEntitlementsQuery,
  useLazyGetAccessEntitlementsQuery,
  useGetPassPricesQuery,
  useCreatePassCheckoutSessionMutation,
  useVerifyPassCheckoutMutation,
} = watercraftPassesSlice;
